import React, { useState, useEffect, useMemo } from 'react';
import { Card, Typography, Row, Col, Select, DatePicker, message, Spin, Tabs, Table, Tag, Badge } from 'antd';
import { DashboardOutlined } from '@ant-design/icons';
import { useAuth } from '../contexts/AuthContext';
import StatisticCard from '../components/Dashboard/StatisticCard';
import SalesChart from '../components/Dashboard/SalesChart';
import PieChart from '../components/Dashboard/PieChart';
import dashboardService, { type DashboardParams, type TodoItemCategory } from '../services/dashboard';
import { SERVICE_TYPE_MAP } from '../config/service-type-map';
import useDocumentTitle from '../hooks/useDocumentTitle';
import dayjs from 'dayjs';
import userService from '../services/user';

const { Title } = Typography;
const { RangePicker } = DatePicker;

const Dashboard: React.FC = () => {
  useDocumentTitle('销售业绩管理');
  const { state, hasRole } = useAuth();
  const isAdmin = hasRole('admin');
  const canViewTodoSeller = isAdmin || hasRole('sales_manager');
  const [timeRange, setTimeRange] = useState<'week' | 'month' | 'quarter' | 'year'>('week');
  const [loading, setLoading] = useState(false);
  const [salesData, setSalesData] = useState<any>(null); // 按销售员的数据
  const [serviceData, setServiceData] = useState<any>(null); // 按业务类型的数据
  const [nationData, setNationData] = useState<any>(null); // 按国家的数据
  const [classData, setClassData] = useState<any>(null); // 按类目数据
  const [newClientsData, setNewClientsData] = useState<any>(null); // 新增客户数据
  const [newClientsSeller, setNewClientsSeller] = useState<string>('');
  const [todoLoading, setTodoLoading] = useState(false);
  const [activeTodoTab, setActiveTodoTab] = useState<'pending_payment' | 'pending_submission' | 'pending_notice' | 'abnormal'>('pending_payment');
  const [todoPagination, setTodoPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [todoData, setTodoData] = useState<Record<string, TodoItemCategory>>({
    pending_payment: { count: 0, data: [] },
    pending_submission: { count: 0, data: [] },
    pending_notice: { count: 0, data: [] },
    abnormal: { count: 0, data: [] },
  });
  const [sellerOptions, setSellerOptions] = useState<{ value: string; label: string }[]>([]);
  const sellerIdForRequest = useMemo(() => {
    if (!isAdmin && state.user?.id) {
      return String(state.user.id);
    }

    return newClientsSeller || undefined;
  }, [isAdmin, state.user?.id, newClientsSeller]);

  // 加载仪表盘数据
  const loadDashboardData = async (params: DashboardParams = {}) => {
    setLoading(true);
    try {
      // 同时加载按销售员和按业务类型的数据
      const [sellerResponse, serviceResponse, newClientsResponse] = await Promise.all([
        dashboardService.getAllDashboardData({
          time_range: timeRange,
          group_by: 'seller',
          seller_id: sellerIdForRequest,
          ...params,
        }),
        dashboardService.getAllDashboardData({
          time_range: timeRange,
          group_by: 'service',
          seller_id: sellerIdForRequest,
          ...params,
        }),
        dashboardService.getNewClients({
          time_range: timeRange,
          seller_id: sellerIdForRequest,
          ...params,
        })
      ]);

      if (sellerResponse.success) {
        setSalesData(sellerResponse.data);
      } else {
        message.error('加载销售员数据失败');
      }

      if (serviceResponse.success) {
        setServiceData(serviceResponse.data);
        setNationData(serviceResponse.data?.nationDistribution || null);
        setClassData(serviceResponse.data?.classDistribution || null);
      } else {
        message.error('加载服务数据失败');
      }

      if (newClientsResponse.success) {
        setNewClientsData(newClientsResponse.data);
      } else {
        message.error('加载新增客户数据失败');
      }
    } catch (error) {
      console.error('加载仪表盘数据失败:', error);
      message.error('加载仪表盘数据失败，请稍后重试');
    } finally {
      setLoading(false);
    }
  };

  // 时间范围变化时重新加载数据
  useEffect(() => {
    loadDashboardData();
  }, [timeRange]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    loadDashboardData();
  }, [sellerIdForRequest]);

  // 待办事项数据加载（从后端过滤所有订单）
  useEffect(() => {
    const loadTodoItems = async () => {
      setTodoLoading(true);
      try {
        const res = await dashboardService.getTodoItems({
          category: activeTodoTab,
          page: todoPagination.current,
          per_page: todoPagination.pageSize,
        });
        if (res.success && res.data) {
          setTodoData(res.data);
          setTodoPagination(prev => ({
            ...prev,
            current: res.pagination.current_page,
            pageSize: res.pagination.per_page,
            total: res.pagination.total,
          }));
        }
      } catch (e) {
        message.error('加载待办事项失败');
      } finally {
        setTodoLoading(false);
      }
    };
    loadTodoItems();
  }, [activeTodoTab, todoPagination.current, todoPagination.pageSize]);

  // 加载销售用户，用于折线下拉显示姓名
  useEffect(() => {
    const loadSellers = async () => {
      try {
        const res = await userService.getUsers();
        if (res.success && Array.isArray(res.data)) {
          const sellerRoleNames = ['sales', 'sales_manager'];
          const options = res.data
            .filter((u: any) => Array.isArray(u.roles) && u.roles.some((r: any) => sellerRoleNames.includes(r.name)))
            .map((u: any) => ({ value: String(u.id), label: u.display_name || u.username || u.email || String(u.id) }));
          setSellerOptions(options);
        }
      } catch (e) {
        // ignore
      }
    };
    loadSellers();
  }, []);

  // 时间范围选择变化
  const handleTimeRangeChange = (newTimeRange: 'week' | 'month' | 'quarter' | 'year') => {
    setTimeRange(newTimeRange);
  };


  // 自定义日期范围选择
  const handleDateRangeChange = (dates: any) => {
    if (dates && dates.length === 2) {
      loadDashboardData({
        start_date: dates[0].format('YYYY-MM-DD'),
        end_date: dates[1].format('YYYY-MM-DD'),
      });
    }
  };

  // 获取格式化的统计数据（使用服务数据）
  const getFormattedStatistics = () => {
    if (!serviceData?.statistics) return [];
    return dashboardService.formatStatisticsForComponents(serviceData.statistics);
  };

  // 获取格式化的销售趋势数据（使用销售员数据）
  const getFormattedSalesTrend = () => {
    if (!salesData?.salesTrend) return { data: [], series: [], groupBy: 'seller' as const };
    return dashboardService.formatSalesTrendForChart(salesData.salesTrend);
  };

  // 获取格式化的业务构成数据（使用服务数据）
  const getFormattedBusinessComposition = () => {
    if (!serviceData?.businessComposition) return [];
    return dashboardService.formatBusinessCompositionForChart(serviceData.businessComposition);
  };

  // 获取格式化的国家分布数据
  const getFormattedNationDistribution = () => {
    if (!nationData?.distribution) return [];
    return dashboardService.formatNationDistributionForChart(nationData);
  };

  // 获取格式化的类目分布数据
  const getFormattedClassDistribution = () => {
    if (!classData?.distribution) return [];
    return dashboardService.formatClassDistributionForChart(classData);
  };

  // 获取新增客户折线数据
  const getFormattedNewClients = () => {
    if (!newClientsData?.chart_data) return [];
    return dashboardService.formatNewClientsForChart(newClientsData);
  };

  const getClientTypeSummary = () => {
    return dashboardService.formatClientTypes(newClientsData || {});
  };

  const renderSalesDashboardContent = () => (
    <Spin spinning={loading}>
      <div>
        {/* 销售统计卡片 */}
        <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
          {getFormattedStatistics().map((stat, index) => (
            <Col xs={24} sm={12} lg={6} key={index}>
              <StatisticCard
                title={stat.title}
                value={stat.value}
                change={stat.change}
                type={stat.type}
                loading={loading}
              />
            </Col>
          ))}
        </Row>

        {/* 销售趋势 + 新增客户折线同排 */}
        <Row gutter={[16, 16]}>
          <Col xs={24} lg={12}>
            <SalesChart
              data={getFormattedSalesTrend().data}
              seriesKeys={getFormattedSalesTrend().series}
              seriesLabels={sellerOptions}
              loading={loading}
              groupBy={getFormattedSalesTrend().groupBy}
              title="销售业绩管理"
              sellerFilterOptions={isAdmin ? sellerOptions : []}
              showSellerSelector={isAdmin}
            />
          </Col>
          <Col xs={24} lg={12}>
            <SalesChart
              data={getFormattedNewClients()}
              loading={loading}
              groupBy="service"
              seriesKeys={['client_count']}
              title="新增客户统计"
              sellerFilterOptions={isAdmin ? sellerOptions : []}
              sellerFilterValue={isAdmin ? newClientsSeller || undefined : undefined}
              onSellerFilterChange={isAdmin ? (val) => setNewClientsSeller(val || '') : undefined}
              showSellerSelector={isAdmin}
              clientTypes={getClientTypeSummary()}
            />
          </Col>
        </Row>

        {/* 三个饼图一行展示 */}
        <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
          <Col xs={24} lg={8}>
            <PieChart
              data={getFormattedBusinessComposition()}
              loading={loading}
              title="业务构成分析"
            />
          </Col>
          <Col xs={24} lg={8}>
            <PieChart
              data={getFormattedNationDistribution()}
              loading={loading}
              title="国家分布"
            />
          </Col>
          <Col xs={24} lg={8}>
            <PieChart
              data={getFormattedClassDistribution()}
              loading={loading}
              title="类目分布"
            />
          </Col>
        </Row>

      </div>
    </Spin>
  );

  const todoTabs = [
    { key: 'pending_payment', label: '待付款' },
    { key: 'pending_submission', label: '待提交' },
    { key: 'pending_notice', label: '待通知' },
    { key: 'abnormal', label: '异常案件' },
  ];

  const todoColumns = [
    {
      title: '国家',
      dataIndex: 'nation',
      key: 'nation',
      width: 100,
      render: (v: any) => v || '-',
    },
    {
      title: '业务名称',
      key: 'business',
      width: 120,
      render: (_: any, record: any) => {
        const svc = record.service || '';
        const ext = (record as any)[svc] || {};
        const sub = ext.sub_service;
        const label = (SERVICE_TYPE_MAP as any)[svc]?.[sub] || sub || '-';
        return label;
      },
    },
    {
      title: '客户名称',
      dataIndex: 'contact_name',
      key: 'contact_name',
      width: 120,
      render: (v: any) => v || '-',
    },
    ...(canViewTodoSeller ? [{
      title: '所属销售',
      dataIndex: 'seller',
      key: 'seller',
      width: 120,
      render: (_: any, record: any) => {
        const sales = record?.sales;
        const byAccessor = sales?.contact_name;
        const byDisplay = sales?.display_name || sales?.username || sales?.email;
        const bySellerName = record?.seller_name;
        return byAccessor || byDisplay || bySellerName || record?.seller || '-';
      },
    }] : []),
    {
      title: '订单号',
      dataIndex: 'order_no',
      key: 'order_no',
      width: 120,
      render: (v: any) => v || '-',
    },
    {
      title: '订单创建日期',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 90,
      render: (v: any) => v ? dayjs(v).format('YYYY-MM-DD') : '-',
    },
    {
      title: '最新状态',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (v: any) => v ? <Tag color="blue">{v}</Tag> : '-',
    },
    {
      title: '付款状态',
      dataIndex: 'payment_status',
      key: 'payment_status',
      width: 100,
    },
    {
      title: '付款日期',
      dataIndex: 'payment_at',
      key: 'payment_at',
      width: 100,
      render: (v: any) => v ? dayjs(v).format('YYYY-MM-DD') : '-',
    },
    {
      title: '截止日期',
      dataIndex: 'due_at',
      key: 'due_at',
      width: 100,
      render: (v: any) => v ? dayjs(v).format('YYYY-MM-DD') : '-',
    },
    {
      title: '备注',
      dataIndex: 'remark',
      key: 'remark',
      width: 250,
      render: (remark: string) => {
        if (!remark) return '-';
        return remark.length > 10 ? `${remark.slice(0, 20)}...` : remark;
      },
    },
    {
      title: '查看',
      dataIndex: 'id',
      key: 'id',
      width: 50,
      render: (id: number) => id ? <a href={`/orders/${id}`} target="_blank" rel="noopener noreferrer">查看</a> : '-',
    },
  ];

  const renderTodoCard = () => (
    <Card title="待办事项" style={{ marginTop: 16 }}>
      <Tabs
        activeKey={activeTodoTab}
        onChange={(key) => {
          setActiveTodoTab(key as 'pending_payment' | 'pending_submission' | 'pending_notice' | 'abnormal');
          setTodoPagination(prev => ({
            ...prev,
            current: 1,
            total: todoData[key]?.count || 0,
          }));
        }}
        items={todoTabs.map(tab => ({
          key: tab.key,
          label: (
            <Badge count={todoData[tab.key]?.count || 0} size="small" color="#f5222d" showZero>
              <span style={{ paddingRight: 12 }}>{tab.label}</span>
            </Badge>
          ),
          children: (
              <Table
                rowKey={(r: any) => r.id as any}
                dataSource={todoData[tab.key]?.data || []}
                columns={todoColumns}
                pagination={{
                  current: activeTodoTab === tab.key ? todoPagination.current : 1,
                  pageSize: todoPagination.pageSize,
                  total: activeTodoTab === tab.key ? todoPagination.total : (todoData[tab.key]?.count || 0),
                  showSizeChanger: false,
                  onChange: (page) => {
                    if (activeTodoTab !== tab.key) {
                      return;
                    }

                    setTodoPagination(prev => ({
                      ...prev,
                      current: page,
                    }));
                  },
                }}
                loading={todoLoading}
                size="small"
                scroll={{ x: 920 }}
              />
          ),
        }))}
      />
    </Card>
  );


  return (
    <div style={{ padding: '24px' }}>
      {/* 标题和时间选择器在一行 */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '24px'
      }}>
        <Title level={4} style={{ margin: 0 }}>
          <DashboardOutlined /> 销售业绩管理
        </Title>
        <div style={{ display: 'flex', gap: 16 }}>
          <Select
            value={timeRange}
            onChange={handleTimeRangeChange}
            style={{ width: 120 }}
            options={[
              { label: '本周', value: 'week' },
              { label: '本月', value: 'month' },
              { label: '本季度', value: 'quarter' },
              { label: '本年', value: 'year' },
            ]}
          />
          <RangePicker onChange={handleDateRangeChange} />
        </div>
      </div>

      <div style={{ marginBottom: 24 }}>
        {renderTodoCard()}
      </div>

      {renderSalesDashboardContent()}

    </div>
  );
};

export default Dashboard;
